import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { filterByPermission } from "@/lib/permissions";
import {
  LayoutDashboard, Users, FileText, ShieldCheck,
  ScrollText, UserCog, LogOut, Menu, X, Building2, Map, DollarSign, Car, Link2, Star, Trophy, Shield, KeyRound, Droplet, Boxes, Briefcase
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_apex-merchants/artifacts/6e4jsq53_New%20FieldProIQ%20logo.PNG";

const ROLE_DISPLAY = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  dispatcher: "Dispatcher",
  branch_manager: "Branch Manager",
};

export default function ManagementLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const canSeeLocations = ["owner", "manager", "admin"].includes(user?.role);
  const canSeeUsers = ["owner", "admin"].includes(user?.role);
  const canSeeAudit = ["manager", "owner", "admin"].includes(user?.role);
  const canSeeFinancials = ["owner", "admin", "manager", "branch_manager"].includes(user?.role);

  const sidebarItems = [
    { to: "/management/approvals",   icon: ShieldCheck,     label: "Approvals",       show: true,                       perm: "mgmt.approvals" },
    { to: "/management/audit",       icon: ScrollText,      label: "Audit Logs",      show: canSeeAudit,                perm: "mgmt.audit" },
    { to: "/management/contentguard", icon: Boxes,          label: "ContentGuard Audit", show: canSeeFinancials,        perm: "mgmt.contentguard" },
    { to: "/management/customers",    icon: Users,           label: "Customers",          show: true,                    perm: "mgmt.customers" },
    { to: "/management",             icon: LayoutDashboard, label: "Dashboard",       end: true, show: true,            perm: "mgmt.dashboard" },
    { to: "/management/driving",     icon: Shield,          label: "Driving Safety",  show: canSeeFinancials,           perm: "mgmt.driving" },
    { to: "/management/requests",    icon: Briefcase,       label: "Jobs",            show: true,                       perm: "mgmt.requests" },
    { to: "/management/leaderboard", icon: Trophy,          label: "Leaderboard",     show: canSeeFinancials,           perm: "mgmt.leaderboard" },
    { to: "/management/locations",   icon: Building2,       label: "Locations",       show: canSeeLocations,            perm: "mgmt.locations" },
    { to: "/management/map",         icon: Map,             label: "Map View",        show: true,                       perm: "mgmt.map" },
    { to: "/management/moisture-equipment", icon: Droplet,  label: "Moisture Equipment", show: canSeeFinancials,        perm: "mgmt.moisture_eq" },
    { to: "/management/financials",  icon: DollarSign,      label: "P&L / Financials", show: canSeeFinancials,          perm: "mgmt.financials" },
    { to: "/management/ratings",     icon: Star,            label: "Ratings",         show: canSeeFinancials,           perm: "mgmt.ratings" },
    { to: "/management/permissions", icon: KeyRound,        label: "Special Permissions", show: canSeeUsers,            perm: "mgmt.permissions" },
    { to: "/management/technicians", icon: Users,           label: "Technicians",     show: true,                       perm: "mgmt.technicians" },
    { to: "/management/users",       icon: UserCog,         label: "Users & Roles",   show: canSeeUsers,                perm: "mgmt.users" },
    { to: "/management/vehicles",    icon: Car,             label: "Vehicles",        show: canSeeFinancials,           perm: "mgmt.vehicles" },
    { to: "/management/xactimate",   icon: Link2,           label: "Estimate Exports", show: canSeeLocations,            perm: "mgmt.xactimate" },
  ];

  const visibleItems = filterByPermission(sidebarItems, user);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-slate-400/30">
        <img src={LOGO_URL} alt="Apex FieldProIQ" className="w-full h-auto object-contain" />
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {visibleItems.map(({ to, icon: Icon, label, end, perm }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-red-600 text-white shadow-sm"
                  : "text-slate-700 hover:bg-slate-900/5 hover:text-slate-900"
              }`
            }
            data-testid={perm ? `mgmt-nav-${perm.replace(/\./g, '-')}` : `mgmt-nav-${label.toLowerCase().replace(/\W+/g, '-')}`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-slate-400/30">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-red-600/15 flex items-center justify-center">
            <span className="text-xs font-bold text-red-600">
              {user?.email?.[0]?.toUpperCase() || "U"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-900 truncate">{user?.email}</p>
            <p className="text-[10px] text-slate-500">{ROLE_DISPLAY[user?.role] || user?.role}</p>
          </div>
        </div>
        {user?.role === "branch_manager" && user?.location && (
          <div className="mb-3 px-2 py-1.5 bg-slate-900/5 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Location</p>
            <p className="text-xs text-slate-700">{user.location.name}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-500/10 text-xs"
          data-testid="mgmt-logout-button"
        >
          <LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      <aside
        className="hidden lg:flex w-56 border-r border-slate-300/70 flex-col fixed h-screen shadow-[1px_0_0_rgba(255,255,255,0.6)_inset]"
        style={{
          background:
            "linear-gradient(180deg, #F1F5F9 0%, #CBD5E1 50%, #94A3B8 100%)",
        }}
        data-testid="mgmt-sidebar"
      >
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside
            className="absolute left-0 top-0 w-64 h-full shadow-xl"
            style={{
              background:
                "linear-gradient(180deg, #F1F5F9 0%, #CBD5E1 50%, #94A3B8 100%)",
            }}
          >
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 p-1 rounded-lg hover:bg-slate-900/10"
            >
              <X className="w-5 h-5 text-slate-700" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-56">
        <header className="glass-header sticky top-0 z-30 lg:hidden px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1.5 rounded-lg hover:bg-slate-900/10"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5 text-slate-700" />
            </button>
            <img src={LOGO_URL} alt="Apex" className="h-12 w-auto object-contain" />
            <div className="w-8" />
          </div>
        </header>

        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
